import {
  RequiredUnknownSchema,
  SchemaDefinition,
  UnknownSchema
} from '@rehaje/core'

import type { SchemaMetadataSpecific, TypedSchema } from '@rehaje/core'

export interface SchemaExpressionSpecific extends SchemaMetadataSpecific {
  /**
   * 默认值
   */
  default?: any
  /**
   * 表达式预期类型
   */
  expect?: SchemaMustacheExpect
  type: 'Schema.Expression'
  /**
   * 具体值
   */
  value?: any
}

export interface SchemaJSExpressionSpecific extends SchemaMetadataSpecific {
  /**
   * 默认值
   */
  default?: any
  /**
   * 表达式预期类型
   */
  expect?: SchemaMustacheExpect
  type: 'Schema.JSExpression'
  /**
   * 具体值
   */
  value?: any
}

export type SchemaMustacheExpect = Exclude<
  TypedSchema,
  | { type: 'Schema.Expression' }
  | { type: 'Schema.JSExpression' }
  | { type: 'Schema.Mustache' }
>

export interface SchemaMustacheSpecific extends SchemaMetadataSpecific {
  /**
   * 默认值
   */
  default?: any
  /**
   * 表达式预期类型
   */
  expect?: SchemaMustacheExpect
  type: 'Schema.Mustache'
  /**
   * 具体值
   */
  value?: any
}

export class SchemaExpressionDefinition extends SchemaDefinition {
  override defaultsMerge(
    value: any,
    schema: SchemaExpressionSpecific,
    initialize: boolean
  ) {
    if (initialize) {
      if (value === undefined) return schema.default
      return value
    }
    if (schema.required && value === undefined) {
      return schema.default
    }
    return value
  }

  override getIn(schema: SchemaExpressionSpecific, path: PropertyKey[]) {
    if (path.length === 0) return schema
    return UnknownSchema
  }

  override infer(
    target: any,
    schema: SchemaExpressionSpecific,
    path: PropertyKey[] = []
  ): TypedSchema {
    if (schema.expect) return super.infer(target, schema.expect, path)
    return { ...schema, value: target }
  }

  override inferOnlySchema(
    schema: SchemaExpressionSpecific,
    path: PropertyKey[]
  ): SchemaExpressionSpecific {
    return {
      ...schema,
      expect: schema.expect
        ? (super.inferOnlySchema(schema.expect, path) as SchemaMustacheExpect)
        : undefined
    }
  }

  override inferTypeScript(schema: SchemaExpressionSpecific): string {
    if (schema?.value !== undefined)
      return super.inferTypeScript(schema.expect ?? RequiredUnknownSchema, [])
    return `${this.interface}${schema.expect ? `| ${super.inferTypeScript(schema.expect, [])}` : ''}`
  }

  override prune(target: any): any {
    return target
  }

  override transform(
    target: any,
    schema: SchemaExpressionSpecific,
    path: PropertyKey[] = []
  ) {
    if (schema?.expect) return super.transform(target, schema.expect, path)
    return target
  }

  override validate(target: any, schema: any): void
  override validate(target: any, schema: SchemaMustacheSpecific): void {
    if (this.validateMetadata(target, schema)) return
    if (this.isRelationSpecific(target)) return
    if (schema.expect) super.validate(target, schema.expect)
  }
}

export class SchemaJSExpressionDefinition extends SchemaDefinition {
  override defaultsMerge(
    value: any,
    schema: SchemaJSExpressionSpecific,
    initialize: boolean
  ) {
    if (initialize) {
      if (value === undefined) return schema.default
      return value
    }
    if (schema.required && value === undefined) {
      return schema.default
    }
    return value
  }

  override getIn(schema: SchemaJSExpressionSpecific, path: PropertyKey[]) {
    if (path.length === 0) return schema
    return UnknownSchema
  }

  override infer(
    target: any,
    schema: SchemaJSExpressionSpecific,
    path: PropertyKey[] = []
  ): TypedSchema {
    if (schema.expect) return super.infer(target, schema.expect, path)
    return { ...schema, value: target }
  }

  override inferOnlySchema(
    schema: SchemaJSExpressionSpecific,
    path: PropertyKey[]
  ): SchemaJSExpressionSpecific {
    return {
      ...schema,
      expect: schema.expect
        ? (super.inferOnlySchema(schema.expect, path) as SchemaMustacheExpect)
        : undefined
    }
  }

  override inferTypeScript(schema: SchemaJSExpressionSpecific): string {
    if (schema?.value !== undefined)
      return super.inferTypeScript(schema.expect ?? RequiredUnknownSchema, [])
    return `${this.interface}${schema.expect ? `| ${super.inferTypeScript(schema.expect, [])}` : ''}`
  }

  override prune(target: any): any {
    return target
  }

  override transform(
    target: any,
    schema: SchemaJSExpressionSpecific,
    path: PropertyKey[] = []
  ) {
    if (schema?.expect) return super.transform(target, schema.expect, path)
    return target
  }

  override validate(target: any, schema: any): void
  override validate(target: any, schema: SchemaMustacheSpecific): void {
    if (this.validateMetadata(target, schema)) return
    if (this.isRelationSpecific(target)) return
    if (schema.expect) super.validate(target, schema.expect)
  }
}

export class SchemaMustacheDefinition extends SchemaDefinition {
  override defaultsMerge(
    value: any,
    schema: SchemaMustacheSpecific,
    initialize: boolean
  ) {
    if (initialize) {
      if (value === undefined) return schema.default
      return value
    }
    if (schema.required && value === undefined) {
      return schema.default
    }
    return value
  }

  override getIn(schema: SchemaMustacheSpecific, path: PropertyKey[]) {
    if (path.length === 0) return schema
    return UnknownSchema
  }

  override infer(
    target: any,
    schema: SchemaMustacheSpecific,
    path: PropertyKey[] = []
  ): TypedSchema {
    if (schema.expect) return super.infer(target, schema.expect, path)
    return { ...schema, value: target }
  }

  override inferOnlySchema(
    schema: SchemaMustacheSpecific,
    path: PropertyKey[]
  ): SchemaMustacheSpecific {
    return {
      ...schema,
      expect: schema.expect
        ? (super.inferOnlySchema(schema.expect, path) as SchemaMustacheExpect)
        : undefined
    }
  }

  override inferTypeScript(schema: SchemaMustacheSpecific): string {
    if (schema?.value !== undefined)
      return super.inferTypeScript(schema.expect ?? RequiredUnknownSchema, [])
    return `${this.interface}${schema?.expect ? `| ${super.inferTypeScript(schema.expect, [])}` : ''}`
  }

  override prune(target: any): any {
    return target
  }

  override transform(
    target: any,
    schema: SchemaMustacheSpecific,
    path: PropertyKey[] = []
  ) {
    if (schema?.expect)
      return super.transform(target, schema.expect, path, parent)
    return target
  }

  override validate(target: any, schema: any): void
  override validate(target: any, schema: SchemaMustacheSpecific): void {
    if (this.validateMetadata(target, schema)) return
    if (this.isRelationSpecific(target)) return
    if (schema.expect) super.validate(target, schema.expect)
  }
}

declare global {
  export interface RehajeSpecifics {
    'Schema.Expression': SchemaExpressionSpecific
    'Schema.JSExpression': SchemaJSExpressionSpecific
    'Schema.Mustache': SchemaMustacheSpecific
  }
}
